import { put, select, takeEvery } from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';

import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IKnmIndexPage } from 'APIPackage/KnmModule/interfaces/IKnmIndexPage';
import { IExport } from 'APIPackage/ImportExportModule/interfaces/IExport';
import { knmDeleteSagaFunc } from 'KnmPackage/MainModule/sagas/knmDeleteSagaFunc';

import { exportPmList } from '../services/pmListFiltersApi';
import { fetchPmFindIndex } from '../services/pmListApi';
import { IPmListFilterParams } from '../interfaces/IPmListFilterParams';
import { getRequestFilterData } from '../common/getRequestFilterData';
import { mapRawToData } from '../common/mapRawToData';
import {
  preventionDeleteKnms,
  preventionGetKnms,
  preventionSetData,
  preventionListExportPreventions,
  preventionGetPmSelectOptions,
} from '../actions/preventionActions';
import {
  getPreventionKnms,
  getPreventionOrder,
  getPreventionPage,
  getPreventionSearchValue,
} from '../selectors/preventionSelectors';
import { PreventionActionsEnum } from '../enums/PreventionActionsEnum';
import { ISelectOption } from '../../../UIPackage/MainModule/components/SelectInput';

const PAGE_SIZE = 50;

export function* watchPreventionSaga() {
  yield takeEvery(PreventionActionsEnum.PREVENTION_GET_KNMS, preventionGetKnmsSaga);
  yield takeEvery(PreventionActionsEnum.PREVENTION_DELETE_KNMS, preventionDeleteKnmsSaga);
  yield takeEvery(PreventionActionsEnum.PREVENTION_GET_KNMS_NEXT_PAGE, preventionGetKnmsNextPageSaga);
  yield takeEvery(PreventionActionsEnum.PREVENTION_LIST_EXPORT_PREVENTIONS, preventionListExportPreventionsSaga);
  yield takeEvery(PreventionActionsEnum.PREVENTION_GET_PM_SELECT_OPTIONS, preventionGetPmSelectOptionsSaga);
}

function* preventionGetKnmsSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const order: string = getPreventionOrder(state);
    const searchString = getPreventionSearchValue(state);
    const requestData: IPmListFilterParams | null = getRequestFilterData(searchString);
    const res: IKnmIndexPage = yield fetchPmFindIndex(0, PAGE_SIZE, requestData, order ?? '');

    yield put(preventionSetData(mapRawToData(res.list), res.page, res.pageCount, res.totalCount, res.rejected));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* preventionGetKnmsNextPageSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const data = getPreventionKnms(state);
    const page = getPreventionPage(state);
    const order = getPreventionOrder(state);
    const searchString = getPreventionSearchValue(state);
    const requestData: IPmListFilterParams | null = getRequestFilterData(searchString);
    const res: IKnmIndexPage = yield fetchPmFindIndex(page + 1, PAGE_SIZE, requestData, order ?? '');

    yield put(
      preventionSetData([...data, ...mapRawToData(res.list)], res.page, res.pageCount, res.totalCount, res.rejected),
    );
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* preventionDeleteKnmsSaga(action: ActionType<typeof preventionDeleteKnms>) {
  const { ids } = action.payload;

  function* funcTry() {
    yield knmDeleteSagaFunc(ids, true);
    yield put(preventionGetKnms());
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* preventionListExportPreventionsSaga(action: ActionType<typeof preventionListExportPreventions>) {
  function* funcTry() {
    const { notify, format, columns, filter } = action.payload;
    const pmFilter: IPmListFilterParams = { ...filter };

    const res: IExport = yield exportPmList(format, pmFilter, columns);

    if (res.id) {
      yield notify(res);
    }
  }
  yield sagaWrapper({
    funcTry,
  });
}

function* preventionGetPmSelectOptionsSaga(action: ActionType<typeof preventionGetPmSelectOptions>) {
  function* funcTry() {
    const { searchValue, callback } = action.payload;
    const filters: IPmListFilterParams = {
      erpId: searchValue,
      ignoreKno: false,
      includeChildKno: false,
    };
    const result: IKnmIndexPage = yield fetchPmFindIndex(0, 10, filters);
    const options: ISelectOption[] = result.list.map(item => ({
      id: item.erpId,
      label: item.erpId,
      value: item.erpId,
    }));

    callback(options);
  }

  yield sagaWrapper({ funcTry });
}
